import { pick } from 'lodash-es';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';
import gql from 'graphql-tag';
import { branchLiveStates } from '@ourbranch/lookups';
import * as Sentry from '@sentry/react';

import { apolloClient } from 'core/apollo-client';
import { track } from 'core/helpers/analytics';
import config from '../../aws-exports';

const Core = SmartyStreetsSDK.core;
const AutocompleteLookup = SmartyStreetsSDK.usAutocompletePro.Lookup;
const StreetLookup = SmartyStreetsSDK.usStreet.Lookup;
const credentials = new Core.SharedCredentials(config.smartyStreetsKey);
const PROXY_FALLBACK_STORAGE_KEY = 'br.address-suggest.use-proxy-fallback';

const ADDRESS_AUTOCOMPLETE = gql`
  query staffAddressAutocomplete(
    $search: String!
    $selected: String
    $maxResults: Int
    $includeOnlyStates: [String!]
    $preferStates: [String!]
    $preferGeolocation: String
  ) {
    addressAutocomplete(
      search: $search
      selected: $selected
      maxResults: $maxResults
      includeOnlyStates: $includeOnlyStates
      preferStates: $preferStates
      preferGeolocation: $preferGeolocation
    ) {
      streetLine
      secondary
      city
      state
      zipcode
      entries
      source
    }
  }
`;

let inMemoryProxySticky = false;

export const resetAddressAutocompleteProxyFallbackForTests = () => {
  inMemoryProxySticky = false;

  try {
    window.sessionStorage.removeItem(PROXY_FALLBACK_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures in tests that replace sessionStorage.
  }
};

const isProxySticky = () => {
  if (inMemoryProxySticky) {
    return true;
  }

  try {
    return window.sessionStorage.getItem(PROXY_FALLBACK_STORAGE_KEY) === '1';
  } catch (_error) {
    return inMemoryProxySticky;
  }
};

const markProxySticky = () => {
  inMemoryProxySticky = true;

  try {
    window.sessionStorage.setItem(PROXY_FALLBACK_STORAGE_KEY, '1');
  } catch (_error) {
    // Some browser modes expose sessionStorage but throw on access.
  }
};

const mapSuggestions = (suggestions = []) =>
  suggestions.map((result) => ({
    ...pick(result, ['city', 'entries', 'secondary', 'state', 'streetLine', 'zipcode']),
    secondary: result.secondary ?? '',
    entries: result.entries ?? 0
  }));

const trackProxyFallback = (trigger, input) => {
  try {
    track('Address Search Proxy Fallback', {
      trigger,
      searchLength: input?.length ?? 0,
      page: window.location.pathname
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

const isUnauthorizedSmartyError = (error) => error?.statusCode === 401 || error?.status === 401;

const getProxySuggestions = async (value, allowedStates) => {
  const { data } = await apolloClient.query({
    query: ADDRESS_AUTOCOMPLETE,
    variables: {
      search: value,
      selected: null,
      maxResults: 5,
      includeOnlyStates: allowedStates?.length ? allowedStates : null,
      preferStates: branchLiveStates,
      preferGeolocation: 'city'
    },
    fetchPolicy: 'no-cache'
  });

  return mapSuggestions(data?.addressAutocomplete ?? []);
};

export default {
  client: Core.buildClient.usAutocompletePro(credentials),
  addressValidatorClient: Core.buildClient.usStreet(credentials),

  async search(value, allowedStates) {
    if (isProxySticky()) {
      trackProxyFallback('sticky', value);
      return getProxySuggestions(value, allowedStates);
    }

    const lookup = new AutocompleteLookup(value);
    lookup.maxResults = 5;
    // Only set includeOnlyStates if we have states to filter by
    // When allowedStates is undefined/null, allow all US states (no filter)
    if (allowedStates?.length) {
      lookup.includeOnlyStates = allowedStates;
    }
    lookup.prefer = branchLiveStates;
    lookup.geolocate = true;
    lookup.geolocatePrecision = 'CITY';

    try {
      const { result: results } = await this.client.send(lookup);

      return mapSuggestions(results);
    } catch (error) {
      if (!isUnauthorizedSmartyError(error)) {
        throw error;
      }

      markProxySticky();
      trackProxyFallback('401', value);
      return getProxySuggestions(value, allowedStates);
    }
  },

  async validateAddress(addressIn) {
    let smartyResponse;
    try {
      const { address, address2, city, state, zip } = addressIn;
      const lookup = new StreetLookup();
      lookup.street = address;
      lookup.secondary = address2;
      lookup.city = city;
      lookup.state = state;
      lookup.zipCode = zip;
      lookup.match = 'strict';

      smartyResponse = await this.addressValidatorClient.send(lookup);

      // on autocomplete selection, our code fills out 'Apt' in the address2 field upon selection
      // so if it's just 'Apt' then it's missing the unit number
      const missingAddress2 = !address2 || !address2?.trim() || address2?.trim() === 'Apt';

      // Check if address is missing unit number via Delivery Point Validation (DPV) footnotes
      // N1 — Address is missing secondary information (apartment, suite, etc.) which IS REQUIRED for delivery
      // https://smartystreets.com/docs/cloud/us-street-api#dpv-footnotes
      const needsUnitNumber =
        missingAddress2 && smartyResponse.lookups?.[0]?.result?.[0]?.analysis?.dpvFootnotes?.includes('N1');

      return {
        results: smartyResponse.lookups,
        needsUnitNumber
      };
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          addressIn,
          smartyResponse,
          failedToValidateAddress: true
        }
      });
      return {
        results: [],
        needsUnitNumber: false
      };
    }
  }
};
